import { Space, Text } from '@procurify/ui'

import {
  AmountFraction,
  AmountFractionType,
} from '@webapp/components/AmountFraction'
import { Price } from '@webapp/core/components/Price'
import { type BillItemSummary } from '@webapp/domain/accountsPayable/entities'
import { useProcurifyIntl } from '@webapp/hooks'
import { type CurrencyName } from '@webapp/models/enums'
import { BillItemPopoverLayout } from './BillItemPopoverLayout'
import messages from './messages'

interface BillItemSummaryReceivedCostContentProps {
  currencyName: CurrencyName
  itemSummary: BillItemSummary
}

export const BillItemSummaryReceivedCostContent = ({
  currencyName,
  itemSummary,
}: BillItemSummaryReceivedCostContentProps) => {
  const intl = useProcurifyIntl()

  const showReceivedQuantity =
    !!itemSummary.receivedQuantity && !!itemSummary.purchasedQuantity

  const renderPrice = (price: number) => (
    <Price mdp price={price} currency={currencyName} fontWeight='normal' />
  )

  return (
    <BillItemPopoverLayout
      title={intl.formatMessage(messages.receivedTitle)}
      bodyRows={[
        {
          id: 'received-cost',
          label: intl.formatMessage(messages.receivedLabel),
          valueComponent: (
            <Space size='xsmall' align='end' justify='start'>
              {showReceivedQuantity && (
                <Space.Item>
                  <Space direction='horizontal' size='xsmall'>
                    <Text>{'('}</Text>
                    <AmountFraction
                      type={AmountFractionType.QUANTITY}
                      amount={itemSummary.receivedQuantity}
                      totalAmount={itemSummary.purchasedQuantity}
                    />
                    <Text>{')'}</Text>
                  </Space>
                </Space.Item>
              )}
              <Space.Item>{renderPrice(itemSummary.receivedCost)}</Space.Item>
            </Space>
          ),
        },
        {
          id: 'previously-billed-cost',
          label: intl.formatMessage(messages.previouslyBilledLabel),
          valueComponent: renderPrice(itemSummary.previouslyBilledCost),
        },
      ]}
      footerRows={[
        {
          id: 'billable-cost',
          label: intl.formatMessage(messages.billableLabel),
          valueComponent: renderPrice(itemSummary.billableCost),
        },
        {
          id: 'billing-cost',
          label: intl.formatMessage(messages.billingLabel),
          valueComponent: renderPrice(itemSummary.currentlyBilledCost),
        },
      ]}
    />
  )
}
